import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  useTheme,
  Paper,
  Tooltip,
  Button,
  CardActionArea,
  Badge,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  StorefrontOutlined,
  LocationOn,
  Phone,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { storesApi, salesApi } from '../services/api';
import { format, subDays } from 'date-fns';
import StoreDetailView from '../components/StoreDetailView';

interface Store {
  id: string;
  name: string;
  address?: string;
  address1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  ownerName?: string;
  active?: number | boolean;
  brand?: string;
  currentEODID?: number;
  lastEODID?: number;
  totalSales?: number;
  orderCount?: number;
  averageOrderValue?: number;
}

interface StoreSales {
  storeId: string;
  totalSales: number;
  orderCount: number;
  averageOrderValue: number;
  trend?: number;
}

const StoresOverview: React.FC = () => {
  const theme = useTheme();
  const [stores, setStores] = useState<Store[]>([]);
  const [storeSales, setStoreSales] = useState<Map<string, StoreSales>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  useEffect(() => {
    fetchStoresAndSales();
  }, []);

  const fetchStoresAndSales = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the batch endpoint to get stores with sales data in one call
      const response = await storesApi.getStoresWithSales(yesterday, 150);

      // Handle different response formats
      let storesWithSales = response.data;

      // New format: { data: [...], pagination: {...} }
      if (response.data.data && Array.isArray(response.data.data)) {
        storesWithSales = response.data.data;
      }
      // Legacy format: { stores: [...] }
      else if (response.data.stores && Array.isArray(response.data.stores)) {
        storesWithSales = response.data.stores;
      }
      // Ensure we have an array
      else if (!Array.isArray(storesWithSales)) {
        console.error('Unexpected response format:', response.data);
        storesWithSales = [];
      }

      // Set stores
      setStores(storesWithSales);

      // Build sales map from the batch response
      // Handle two possible formats:
      // 1. Optimized format: { id, name, totalSales, orderCount, averageOrderValue, ... }
      // 2. Nested format: { id, name, sales: { totalSales, orderCount, averageOrderValue } }
      const salesMap = new Map<string, StoreSales>();
      storesWithSales.forEach((store: any) => {
        // Check if sales data is nested or flat
        const salesData = store.sales || store;

        const totalSales = parseFloat(salesData.totalSales) || 0;
        const orderCount = parseInt(salesData.orderCount) || 0;
        const averageOrderValue = parseFloat(salesData.averageOrderValue) || 0;

        salesMap.set(store.id, {
          storeId: store.id,
          totalSales,
          orderCount,
          averageOrderValue,
          trend: 0 // Would need to calculate trend separately
        });
      });

      setStoreSales(salesMap);

    } catch (err) {
      console.error('Error fetching stores:', err);
      setError('Failed to load stores data');
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store => {
    const matchesSearch = 
      (store.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (store.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (store.city || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBrand = selectedBrand === 'all' || store.brand === selectedBrand;
    const matchesState = selectedState === 'all' || store.state === selectedState;
    
    return matchesSearch && matchesBrand && matchesState;
  });

  const getBrands = () => {
    const brands = new Set(stores.map(s => s.brand));
    return Array.from(brands);
  };

  const getStates = () => {
    const states = new Set(stores.map(s => s.state));
    return Array.from(states).sort();
  };

  const StoreCard: React.FC<{ store: Store }> = ({ store }) => {
    const sales = storeSales.get(store.id);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        whileHover={{ y: -8 }}
      >
        <Card
          sx={{
            height: '100%',
            position: 'relative',
            background: 'linear-gradient(145deg, rgba(40,35,36,0.95) 0%, rgba(28,24,25,0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.08)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            overflow: 'visible',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: `0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
              '& .store-badge': {
                transform: 'scale(1.1) rotate(5deg)',
              },
              '&::before': {
                opacity: 1,
              }
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
              opacity: 0,
              transition: 'opacity 0.4s ease',
            }
          }}
          onClick={() => setSelectedStore(store)}
        >
          <CardContent sx={{ p: 3, position: 'relative' }}>
            <Box mb={3}>
              <Box display="flex" alignItems="flex-start" gap={2} mb={1.5}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <StorefrontOutlined sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                </Box>

                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="h6"
                    component="div"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.125rem',
                      lineHeight: 1.3,
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {store.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                      }}
                    >
                      #{store.id}
                    </Typography>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: 'text.secondary',
                        opacity: 0.3,
                      }}
                    />
                    <Chip
                      label={store.brand}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: store.brand === "Happy's Pizza"
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha('#4CAF50', 0.2),
                        color: store.brand === "Happy's Pizza"
                          ? theme.palette.primary.light
                          : '#4CAF50',
                        border: `1px solid ${store.brand === "Happy's Pizza"
                          ? alpha(theme.palette.primary.main, 0.3)
                          : alpha('#4CAF50', 0.3)}`,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {((store.city && store.state) || store.phone) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  background: alpha('#000', 0.2),
                  border: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                {store.city && store.state && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {store.city}, {store.state}
                    </Typography>
                  </Box>
                )}

                {store.phone && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {store.phone}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {sales && (
              <Box>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                          <AttachMoney sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Today's Sales
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.light,
                            fontSize: '1.5rem',
                          }}
                        >
                          ${sales.totalSales.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                          <Receipt sx={{ fontSize: 14, color: theme.palette.info.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Orders
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1.5rem',
                          }}
                        >
                          {sales.orderCount}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${alpha('#fff', 0.06)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                        }}
                      >
                        Avg Order
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        ${sales.averageOrderValue.toFixed(2)}
                      </Typography>
                    </Box>

                    {sales.trend !== undefined && sales.trend !== 0 && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          background: sales.trend > 0
                            ? alpha('#4CAF50', 0.15)
                            : alpha('#ff5252', 0.15),
                          border: `1px solid ${sales.trend > 0
                            ? alpha('#4CAF50', 0.3)
                            : alpha('#ff5252', 0.3)}`,
                        }}
                      >
                        {sales.trend > 0 ? (
                          <TrendingUp sx={{ fontSize: 16, color: '#4CAF50' }} />
                        ) : (
                          <TrendingDown sx={{ fontSize: 16, color: '#ff5252' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: sales.trend > 0 ? '#4CAF50' : '#ff5252',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        >
                          {Math.abs(sales.trend).toFixed(1)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchStoresAndSales}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  // If a store is selected, show the detail view
  if (selectedStore) {
    return (
      <StoreDetailView 
        store={selectedStore} 
        onBack={() => setSelectedStore(null)} 
      />
    );
  }

  return (
    <Box>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          mb={4}
          sx={{
            position: 'relative',
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <StorefrontOutlined sx={{ fontSize: 32, color: '#fff' }} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}, #fff)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5,
                }}
              >
                Store Locations
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Monitor performance across all {stores.length} locations • Click any card for detailed analytics
              </Typography>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Filters and Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha('#fff', 0.08)}`,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Search stores by name, ID, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: alpha('#000', 0.2),
                  }
                }}
              />
            </Grid>

            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Brand"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                SelectProps={{ native: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: alpha('#000', 0.2),
                  }
                }}
              >
                <option value="all">All Brands</option>
                {getBrands().map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="medium"
                label="State"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                SelectProps={{ native: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: alpha('#000', 0.2),
                  }
                }}
              >
                <option value="all">All States</option>
                {getStates().map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={2}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="medium"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    borderRadius: 2,
                    border: `1px solid ${alpha('#fff', 0.1)}`,
                    '&.Mui-selected': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      color: '#fff',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <GridViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={6} md={2}>
              <Tooltip title="Refresh data">
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={fetchStoresAndSales}
                  startIcon={<Refresh />}
                  sx={{
                    height: '56px',
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      background: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: theme.palette.primary.main,
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Active Stores
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: theme.palette.primary.light }}>
                      {filteredStores.length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: alpha(theme.palette.primary.main, 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <StorefrontOutlined sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.15)} 0%, ${alpha('#4CAF50', 0.05)} 100%)`,
                  border: `1px solid ${alpha('#4CAF50', 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: '#4CAF50',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Total Sales Today
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: '#4CAF50' }}>
                      ${Array.from(storeSales.values())
                        .reduce((sum, s) => sum + s.totalSales, 0)
                        .toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: alpha('#4CAF50', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AttachMoney sx={{ fontSize: 32, color: '#4CAF50' }} />
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha('#2196F3', 0.15)} 0%, ${alpha('#2196F3', 0.05)} 100%)`,
                  border: `1px solid ${alpha('#2196F3', 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: '#2196F3',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Total Orders Today
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: '#2196F3' }}>
                      {Array.from(storeSales.values())
                        .reduce((sum, s) => sum + s.orderCount, 0)
                        .toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: alpha('#2196F3', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Receipt sx={{ fontSize: 32, color: '#2196F3' }} />
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha('#FF9800', 0.15)} 0%, ${alpha('#FF9800', 0.05)} 100%)`,
                  border: `1px solid ${alpha('#FF9800', 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: '#FF9800',
                  }
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      States
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: '#FF9800' }}>
                      {getStates().length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: alpha('#FF9800', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocationOn sx={{ fontSize: 32, color: '#FF9800' }} />
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </motion.div>

      {/* Store Cards */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3}>
              {filteredStores.map((store, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={store.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                  >
                    <StoreCard store={store} />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box>
              {filteredStores.map((store, index) => (
                <motion.div
                  key={store.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.03 }}
                >
                  <Box mb={3}>
                    <StoreCard store={store} />
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {filteredStores.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
              borderRadius: 4,
              background: alpha(theme.palette.background.paper, 0.4),
              border: `1px solid ${alpha('#fff', 0.06)}`,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 4,
                background: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
              }}
            >
              <StorefrontOutlined sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.6 }} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              No Stores Found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No stores match your current search criteria. Try adjusting your filters.
            </Typography>
          </Box>
        </motion.div>
      )}
    </Box>
  );
};

export default StoresOverview;